/**
 * RepairIntelligence — /v3/intel/repair
 *
 * V3-native surface for BP-003 Repair Intelligence. Thin premium
 * presentation layer over the existing engine — no new business logic.
 *
 * Flow:
 *   1. Pick a property (or auto-select the first one)
 *   2. Choose an existing estimate or start a fresh draft
 *   3. Add line items — from the Template Library or blank rows
 *   4. Live totals update on every save (backend recalculates)
 *   5. Publish → writes grand_total back to Property.estimated_repairs
 *   6. AI sanity-check + PDF export
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, Loader2, Save, Send, Sparkles, Trash2, Plus,
  BookOpen, ArrowUpRight, FileDown, Hammer, AlertTriangle,
  Search, CheckCircle2, Percent,
} from "lucide-react";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";
import useSelectedProperty from "@/hooks/useSelectedProperty";

// ─────────────────────────────────────────── constants ──────────────────

const DEFAULT_ITEM = {
  room: "kitchen", category: "kitchen",
  description: "", qty: "1", unit: "each",
  unit_cost: "0", notes: "", photo_ids: [],
};

const HEALTH = {
  strong:   { color: "#10B981", label: "HEALTHY" },
  review:   { color: "#F59E0B", label: "REVIEW" },
  overload: { color: "#DC2626", label: "TOO HEAVY" },
};

// Pretty labels for room / category / unit enums.
const pretty = (s) => (s || "").replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ─────────────────────────────────────────── helpers ────────────────────

function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtMoneyFull(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

// A repair-pct-of-ARV score for the ring: healthier = higher score.
//   0-15%    → 90-100  (excellent)
//   15-25%   → 70-90   (good)
//   25-35%   → 45-70   (review)
//   35-50%   → 20-45   (heavy)
//   50%+     → 0-20    (overload)
function healthScore(pct) {
  const p = Math.max(0, Math.min(1, Number(pct) || 0));
  if (p <= 0.15) return Math.round(100 - (p / 0.15) * 10);
  if (p <= 0.25) return Math.round(90 - ((p - 0.15) / 0.10) * 20);
  if (p <= 0.35) return Math.round(70 - ((p - 0.25) / 0.10) * 25);
  if (p <= 0.50) return Math.round(45 - ((p - 0.35) / 0.15) * 25);
  return Math.max(0, Math.round(20 - ((p - 0.50) / 0.50) * 20));
}
function healthMeta(pct) {
  const p = Number(pct) || 0;
  if (p <= 0.25) return HEALTH.strong;
  if (p <= 0.35) return HEALTH.review;
  return HEALTH.overload;
}
function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ""; }
}

// ─────────────────────────────────────────── page ──────────────────────

export default function RepairIntelligence() {
  const [enums, setEnums] = useState({ rooms: [], categories: [], units: [] });
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const { propertyId, setPropertyId } = useSelectedProperty(properties);

  const [estimates, setEstimates] = useState([]);
  const [estimate, setEstimate] = useState(null);       // active estimate doc
  const [items, setItems] = useState([]);
  const [name, setName] = useState("Repair Estimate");
  const [propertySqft, setPropertySqft] = useState("");
  const [propertyArv, setPropertyArv] = useState("");

  const [loadingProps, setLoadingProps] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [tplDrawer, setTplDrawer] = useState(false);

  const propertyMap = useMemo(() => {
    const m = {}; properties.forEach((p) => { m[p.id] = p; }); return m;
  }, [properties]);

  // Bootstrap: load enums, templates, and workspace properties.
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/v3/repairs/enums").then((r) => r.data),
      api.get("/v3/repairs/templates").then((r) => r.data),
      api.get("/v3/properties?limit=200").then((r) => r.data),
    ]).then(([e, t, p]) => {
      if (!alive) return;
      setEnums(e || { rooms: [], categories: [], units: [] });
      setTemplates(t?.items || []);
      const list = p?.items || [];
      setProperties(list);
    }).catch(() => toast.error("Couldn't load Repair Intelligence"))
      .finally(() => { if (alive) setLoadingProps(false); });
    return () => { alive = false; };
  }, []);

  // Load estimates for the selected property. Auto-adopt the most recent
  // draft, or spin up a blank one if none exist.
  const loadEstimates = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/v3/properties/${pid}/repairs`);
      const list = data?.items || [];
      setEstimates(list);
      const draft = list.find((e) => e.status === "draft") || list[0] || null;
      if (draft) {
        adoptEstimate(draft);
      } else {
        // Reset to a blank draft for this property.
        const p = propertyMap[pid];
        setEstimate(null);
        setItems([]);
        setName("Repair Estimate");
        setPropertySqft(p?.sqft ? String(p.sqft) : "");
        setPropertyArv(p?.estimated_arv ? String(p.estimated_arv) : "");
      }
    } catch {
      setEstimates([]);
    }
  }, [propertyMap]);

  useEffect(() => { if (propertyId) loadEstimates(propertyId); }, [propertyId, loadEstimates]);

  const adoptEstimate = (doc) => {
    setEstimate(doc);
    setName(doc.name || "Repair Estimate");
    setItems((doc.items || []).map((i) => ({ ...i, notes: i.notes || "", photo_ids: i.photo_ids || [] })));
    setPropertySqft(doc.property_sqft ? String(doc.property_sqft) : "");
    setPropertyArv(doc.property_arv ? String(doc.property_arv) : "");
  };

  const updateItem = (idx, patch) => {
    setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeItem = (idx) => setItems((rows) => rows.filter((_, i) => i !== idx));
  const addBlank = () => setItems((rows) => [...rows, { ...DEFAULT_ITEM }]);
  const addFromTemplate = (tpl) => {
    setItems((rows) => [
      ...rows,
      {
        room: enums.rooms?.[0] || "other",
        category: tpl.category,
        description: tpl.description,
        qty: String(tpl.default_qty),
        unit: tpl.default_unit,
        unit_cost: String(tpl.default_unit_cost),
        notes: "", photo_ids: [],
      },
    ]);
    setTplDrawer(false);
    toast.success("Added from library");
  };

  const isPublished = estimate?.status === "published";

  const hasBlankDesc = items.some((r) => !String(r.description || "").trim());

  const save = useCallback(async () => {
    if (!propertyId) { toast.error("Pick a property first."); return; }
    if (isPublished) {
      toast.error("Published estimates are immutable. Duplicate to edit.");
      return;
    }
    // Guard: require description + non-zero qty*unit_cost per row
    const invalid = items.find((r) => !String(r.description || "").trim());
    if (invalid) { toast.error("Every line item needs a description."); return; }

    setSaving(true);
    try {
      const payload = {
        name,
        property_sqft: propertySqft ? Number(propertySqft) : null,
        property_arv: propertyArv || null,
        items: items.map((r) => ({
          room: r.room, category: r.category,
          description: r.description,
          qty: String(r.qty || "0"),
          unit: r.unit,
          unit_cost: String(r.unit_cost || "0"),
          notes: r.notes || null,
          photo_ids: r.photo_ids || [],
        })),
      };
      let doc;
      if (estimate) {
        ({ data: doc } = await api.patch(`/v3/repairs/${estimate.id}`, payload));
      } else {
        ({ data: doc } = await api.post(`/v3/properties/${propertyId}/repairs`, payload));
      }
      adoptEstimate(doc);
      // Refresh side list
      loadEstimates(propertyId);
      toast.success(estimate ? "Estimate updated" : "Estimate saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [propertyId, isPublished, items, name, propertySqft, propertyArv, estimate, loadEstimates]);

  const publish = useCallback(async () => {
    if (!estimate) { toast.error("Save a draft first."); return; }
    if (isPublished) { toast("Already published"); return; }
    if (!window.confirm(
      `Publish "${estimate.name}"? The grand total will overwrite this property's estimated_repairs and the estimate becomes immutable.`,
    )) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/v3/repairs/${estimate.id}/publish`);
      adoptEstimate(data);
      loadEstimates(propertyId);
      toast.success("Published — Property KPIs updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Publish failed");
    } finally {
      setPublishing(false);
    }
  }, [estimate, isPublished, propertyId, loadEstimates]);

  const runAi = useCallback(async () => {
    if (!estimate) { toast.error("Save a draft first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/repairs/${estimate.id}/ai-analysis`);
      adoptEstimate(data);
      toast.success("AI sanity check complete");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI analysis failed");
    } finally {
      setAiLoading(false);
    }
  }, [estimate]);

  const exportPdf = useCallback(() => {
    if (!estimate) { toast.error("Save a draft first."); return; }
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/repairs/${estimate.id}/export/pdf`,
      "_blank",
    );
  }, [estimate]);

  const startNew = () => {
    setEstimate(null);
    setItems([]);
    setName("Repair Estimate — New Version");
    const p = propertyMap[propertyId];
    setPropertySqft(p?.sqft ? String(p.sqft) : "");
    setPropertyArv(p?.estimated_arv ? String(p.estimated_arv) : "");
  };

  // Live subtotal (until backend re-computes on save). Keeps UI snappy.
  const localSubtotal = useMemo(() => {
    return items.reduce((sum, r) => {
      const q = Number(r.qty) || 0;
      const c = Number(r.unit_cost) || 0;
      return sum + q * c;
    }, 0);
  }, [items]);

  const totals = estimate?.totals || null;
  const grand = totals ? Number(totals.grand_total_rehab) : localSubtotal * 1.10;
  const pctArv = totals?.repair_pct_of_arv
    ? Number(totals.repair_pct_of_arv)
    : (propertyArv ? grand / Number(propertyArv) : 0);
  const perSqft = totals?.cost_per_sqft
    ? Number(totals.cost_per_sqft)
    : (propertySqft ? grand / Number(propertySqft) : 0);
  const health = healthMeta(pctArv);
  const score = healthScore(pctArv);
  const byCategory = totals?.by_category || {};
  const maxCat = Math.max(...Object.values(byCategory).map(Number), 1);

  return (
    <div className="space-y-6" data-testid="repair-intel-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
            Intelligence Engine
          </div>
          <h1 className="mt-1 text-[32px] font-bold text-slate-900 tracking-tight leading-tight">
            Repair Intelligence
          </h1>
          <p className="mt-2 text-slate-600 text-[14px] max-w-2xl">
            Itemized, contractor-ready repair estimates. Publish to auto-update Property
            KPIs and lock the grand total.
          </p>
        </div>
        <PropertyPicker
          properties={properties}
          value={propertyId}
          onChange={setPropertyId}
          loading={loadingProps}
        />
      </div>

      {/* Empty state */}
      {!loadingProps && properties.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"
             data-testid="repair-empty-no-props">
          <Hammer className="w-8 h-8 mx-auto mb-3 text-amber-500" />
          <div className="text-slate-900 font-bold text-lg">No properties yet</div>
          <div className="text-sm text-slate-500 mt-1 mb-5">
            Add a property first — repair estimates attach to individual deals.
          </div>
          <Link to="/v3/crm/pipeline"
                className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md hover:shadow-lg"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            Open Pipeline <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {propertyId && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)] gap-4">
          {/* ─── LINE ITEMS TABLE ─── */}
          <section
            data-testid="repair-items-panel"
            className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden"
          >
            <div className="h-1 w-full" style={{ backgroundColor: "#F59E0B" }} />
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
              <div className="h-8 w-8 rounded-lg grid place-items-center text-white shadow-md shrink-0"
                   style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                <Hammer className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600 leading-none">
                  {estimate ? `Estimate v${estimate.version}` : "New Draft"} · {estimate?.status || "draft"}
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isPublished}
                  data-testid="repair-name-input"
                  className="mt-0.5 w-full bg-transparent text-[15px] font-bold text-slate-900 leading-tight focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-md px-1 -mx-1 disabled:opacity-70"
                />
              </div>
              {estimates.length > 0 && (
                <EstimateSelector
                  estimates={estimates}
                  currentId={estimate?.id}
                  onPick={(e) => adoptEstimate(e)}
                  onNew={startNew}
                />
              )}
            </div>

            {/* Property basis inputs */}
            <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-2 gap-3">
              <FormGroup label="Property Sqft (for cost/sqft)">
                <input
                  type="number" min="0" value={propertySqft}
                  onChange={(e) => setPropertySqft(e.target.value)}
                  disabled={isPublished}
                  data-testid="repair-sqft-input"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70"
                />
              </FormGroup>
              <FormGroup label="ARV (for % of ARV)">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input
                    type="number" min="0" value={propertyArv}
                    onChange={(e) => setPropertyArv(e.target.value)}
                    disabled={isPublished}
                    data-testid="repair-arv-input"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-6 pr-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70"
                  />
                </div>
              </FormGroup>
            </div>

            {/* Line items */}
            <div className="divide-y divide-slate-100" data-testid="repair-items-list">
              {items.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="mx-auto h-12 w-12 rounded-xl grid place-items-center bg-amber-50 border border-amber-200 mb-3">
                    <Hammer className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-slate-900 font-bold">No repair items yet</div>
                  <div className="text-sm text-slate-500 mt-1 mb-4">
                    Add items from the Template Library or start from scratch.
                  </div>
                  <div className="inline-flex gap-2">
                    <button type="button"
                            data-testid="repair-add-from-library-empty"
                            onClick={() => setTplDrawer(true)}
                            disabled={isPublished}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-2 disabled:opacity-60">
                      <BookOpen className="w-4 h-4" /> Add from Library
                    </button>
                    <button type="button"
                            data-testid="repair-add-blank-empty"
                            onClick={addBlank}
                            disabled={isPublished}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-2 disabled:opacity-60">
                      <Plus className="w-4 h-4" /> Blank line
                    </button>
                  </div>
                </div>
              ) : (
                items.map((row, idx) => (
                  <RepairRow
                    key={idx} row={row} idx={idx} enums={enums}
                    onChange={(patch) => updateItem(idx, patch)}
                    onRemove={() => removeItem(idx)}
                    disabled={isPublished}
                  />
                ))
              )}
            </div>

            {/* Row actions */}
            {items.length > 0 && (
              <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                <button type="button"
                        data-testid="repair-add-from-library"
                        onClick={() => setTplDrawer(true)}
                        disabled={isPublished}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-1.5 disabled:opacity-60">
                  <BookOpen className="w-4 h-4" /> Library
                </button>
                <button type="button"
                        data-testid="repair-add-blank"
                        onClick={addBlank}
                        disabled={isPublished}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-1.5 disabled:opacity-60">
                  <Plus className="w-4 h-4" /> Blank line
                </button>
                <div className="flex-1" />
                <div className="text-[11px] text-slate-500 tabular-nums">
                  {items.length} item{items.length === 1 ? "" : "s"} · Subtotal {fmtMoneyFull(localSubtotal)}
                </div>
              </div>
            )}

            {/* Bottom actions */}
            <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
              <button type="button"
                      data-testid="repair-save-btn"
                      onClick={save}
                      disabled={saving || publishing || isPublished || items.length === 0 || hasBlankDesc}
                      title={hasBlankDesc ? "Every line item needs a description" : ""}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold px-3.5 py-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Draft
              </button>
              <button type="button"
                      data-testid="repair-publish-btn"
                      onClick={publish}
                      disabled={publishing || !estimate || isPublished}
                      className="inline-flex items-center gap-1.5 rounded-lg text-white text-[13px] font-semibold px-3.5 py-2 shadow-md disabled:opacity-60"
                      style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {isPublished ? "Published" : "Publish"}
              </button>
              <div className="flex-1" />
              <button type="button"
                      data-testid="repair-ai-btn"
                      onClick={runAi}
                      disabled={aiLoading || !estimate}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[12.5px] font-semibold px-3 py-1.5 disabled:opacity-60">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                AI Sanity Check
              </button>
              <button type="button"
                      data-testid="repair-pdf-btn"
                      onClick={exportPdf}
                      disabled={!estimate}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold px-3 py-1.5 disabled:opacity-60">
                <FileDown className="w-4 h-4" /> PDF
              </button>
            </div>

            {/* AI recommendation display */}
            {estimate?.ai_recommendation && (
              <div className="px-5 py-4 border-t border-slate-100 bg-amber-50/40"
                   data-testid="repair-ai-recommendation">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1.5">
                  <Sparkles className="w-3 h-3" /> AI Sanity Check
                </div>
                {estimate.ai_recommendation.summary && (
                  <div className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-line">
                    {estimate.ai_recommendation.summary}
                  </div>
                )}
                {Array.isArray(estimate.ai_recommendation.flags) && estimate.ai_recommendation.flags.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {estimate.ai_recommendation.flags.map((f, i) => (
                      <li key={i} className="text-[12px] text-slate-700 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                        <span>{typeof f === "string" ? f : f.message || JSON.stringify(f)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>

          {/* ─── LIVE TOTALS PANEL ─── */}
          <section
            data-testid="repair-totals-panel"
            className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden self-start sticky lg:top-4"
          >
            <div className="h-1 w-full" style={{ backgroundColor: health.color }} />
            <div className="p-5 flex items-center gap-4">
              <DailyBusinessScore
                score={score}
                size="sm"
                label="Repair health"
                testid="repair-health-ring"
              />
              <div className="min-w-0 flex-1">
                <div
                  data-testid="repair-health-chip"
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ring-1"
                  style={{
                    backgroundColor: `${health.color}15`,
                    color: health.color,
                    borderColor: `${health.color}40`,
                  }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: health.color }} />
                  {health.label}
                </div>
                <div className="mt-1.5 text-[26px] font-bold text-slate-900 tabular-nums leading-none"
                     data-testid="repair-grand-total">
                  {fmtMoneyFull(grand)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Grand total {totals ? "(with contingency)" : "· preview until saved"}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 grid grid-cols-2 gap-2 p-3">
              <KpiTile
                label="% of ARV" color={health.color} icon={Percent}
                value={fmtPct(pctArv)} testid="repair-kpi-pctArv"
              />
              <KpiTile
                label="$ / sqft" color="#3B82F6"
                value={perSqft ? `$${perSqft.toFixed(2)}` : "—"} testid="repair-kpi-perSqft"
              />
              <KpiTile
                label="Subtotal" color="#334155"
                value={fmtMoney(totals?.total_repair_cost ?? localSubtotal)}
                testid="repair-kpi-subtotal"
              />
              <KpiTile
                label="Contingency" color="#8B5CF6"
                value={
                  totals?.contingency_amount
                    ? fmtMoney(totals.contingency_amount)
                    : fmtMoney(localSubtotal * 0.10)
                }
                testid="repair-kpi-contingency"
              />
            </div>

            {Object.keys(byCategory).length > 0 && (
              <div className="border-t border-slate-100 p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Category breakdown
                </div>
                <div className="space-y-1.5" data-testid="repair-category-breakdown">
                  {Object.entries(byCategory)
                    .sort((a, b) => Number(b[1]) - Number(a[1]))
                    .map(([cat, val]) => {
                      const w = Math.max(4, Math.round((Number(val) / maxCat) * 100));
                      return (
                        <div key={cat} className="flex items-center gap-2"
                             data-testid={`repair-cat-${cat}`}>
                          <div className="w-20 text-[11px] font-semibold text-slate-700 truncate">
                            {pretty(cat)}
                          </div>
                          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full"
                                 style={{ width: `${w}%`, backgroundColor: "#F59E0B" }} />
                          </div>
                          <div className="w-16 text-right text-[11px] font-bold tabular-nums text-slate-900">
                            {fmtMoney(val)}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {isPublished && (
              <div className="border-t border-slate-100 px-4 py-3 bg-emerald-50/60 flex items-center gap-2 text-[11.5px] text-emerald-800"
                   data-testid="repair-published-banner">
                <CheckCircle2 className="w-4 h-4" />
                Published · this estimate is locked. Property KPIs are live.
              </div>
            )}
          </section>
        </div>
      )}

      {/* Template drawer */}
      {tplDrawer && (
        <TemplateDrawer
          templates={templates}
          onPick={addFromTemplate}
          onClose={() => setTplDrawer(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────── Repair Row ────────────────

function RepairRow({ row, idx, enums, onChange, onRemove, disabled }) {
  const lineTotal = (Number(row.qty) || 0) * (Number(row.unit_cost) || 0);
  return (
    <div className="p-3 grid grid-cols-12 gap-2 items-center hover:bg-amber-50/40 transition-colors"
         data-testid={`repair-row-${idx}`}>
      <select value={row.room}
              disabled={disabled}
              onChange={(e) => onChange({ room: e.target.value })}
              data-testid={`repair-row-${idx}-room`}
              className="col-span-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70">
        {enums.rooms?.map((r) => <option key={r} value={r}>{pretty(r)}</option>)}
      </select>
      <select value={row.category}
              disabled={disabled}
              onChange={(e) => onChange({ category: e.target.value })}
              data-testid={`repair-row-${idx}-category`}
              className="col-span-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70">
        {enums.categories?.map((c) => <option key={c} value={c}>{pretty(c)}</option>)}
      </select>
      <input type="text"
             value={row.description}
             disabled={disabled}
             onChange={(e) => onChange({ description: e.target.value })}
             placeholder="Description"
             data-testid={`repair-row-${idx}-description`}
             className="col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70" />
      <input type="number" min="0" step="0.1"
             value={row.qty}
             disabled={disabled}
             onChange={(e) => onChange({ qty: e.target.value })}
             data-testid={`repair-row-${idx}-qty`}
             className="col-span-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70" />
      <select value={row.unit}
              disabled={disabled}
              onChange={(e) => onChange({ unit: e.target.value })}
              data-testid={`repair-row-${idx}-unit`}
              className="col-span-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70">
        {enums.units?.map((u) => <option key={u} value={u}>{u.replaceAll("_", " ")}</option>)}
      </select>
      <input type="number" min="0" step="0.01"
             value={row.unit_cost}
             disabled={disabled}
             onChange={(e) => onChange({ unit_cost: e.target.value })}
             placeholder="$"
             data-testid={`repair-row-${idx}-unitCost`}
             className="col-span-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-70" />
      <div className="col-span-1 text-right text-[12px] font-bold tabular-nums text-slate-900"
           data-testid={`repair-row-${idx}-linetotal`}>
        {fmtMoney(lineTotal)}
      </div>
      <button type="button" onClick={onRemove} disabled={disabled}
              data-testid={`repair-row-${idx}-remove`}
              className="col-span-12 md:col-auto text-slate-400 hover:text-rose-600 justify-self-end disabled:opacity-30">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────── property picker ──────────

function PropertyPicker({ properties, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const selected = properties.find((p) => p.id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return properties;
    const t = q.trim().toLowerCase();
    return properties.filter((p) => `${p.address?.street || ""} ${p.address?.city || ""}`.toLowerCase().includes(t));
  }, [properties, q]);
  return (
    <div ref={boxRef} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
              disabled={loading || properties.length === 0}
              data-testid="repair-property-picker"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white hover:border-amber-400 px-3 py-2 text-[13px] font-semibold text-slate-800 shadow-sm min-w-[240px] justify-between disabled:opacity-60">
        <span className="truncate text-left">
          {loading ? "Loading…" : selected ? `${selected.address?.street || "Property"} · ${selected.address?.state || ""}` : "Select property"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div data-testid="repair-property-picker-menu"
             className="absolute right-0 top-full mt-1 w-[320px] max-h-[360px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl z-30 flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search address, city…" autoFocus
                   className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-[12px] text-slate-500 text-center">No matches.</div>
            ) : filtered.map((p) => (
              <button key={p.id} type="button"
                      data-testid={`repair-property-picker-opt-${p.id}`}
                      onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                      className={`w-full text-left px-3 py-2 text-[12.5px] hover:bg-amber-50 border-b border-slate-50 ${p.id === value ? "bg-amber-50/60" : ""}`}>
                <div className="font-semibold text-slate-900 truncate">{p.address?.street || "—"}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── estimate selector ───────

function EstimateSelector({ estimates, currentId, onPick, onNew }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
              data-testid="repair-estimate-selector"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white hover:border-amber-400 px-2 py-1 text-[11.5px] font-semibold text-slate-700">
        Versions ({estimates.length}) <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-slate-200 bg-white shadow-xl z-30"
             data-testid="repair-estimate-selector-menu">
          <button type="button" onClick={() => { onNew(); setOpen(false); }}
                  data-testid="repair-estimate-new"
                  className="w-full text-left px-3 py-2 text-[12px] hover:bg-amber-50 flex items-center gap-1.5 border-b border-slate-100 font-semibold text-amber-700">
            <Plus className="w-3.5 h-3.5" /> Start a new draft
          </button>
          <div className="max-h-[240px] overflow-y-auto">
            {estimates.map((e) => (
              <button key={e.id} type="button"
                      data-testid={`repair-estimate-opt-${e.id}`}
                      onClick={() => { onPick(e); setOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-[12px] hover:bg-amber-50 border-b border-slate-50 ${e.id === currentId ? "bg-amber-50/60" : ""}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${e.status === "published" ? "bg-emerald-500" : "bg-amber-500"}`} />
                  <div className="font-semibold text-slate-900 truncate flex-1">{e.name}</div>
                  <div className="text-[10px] text-slate-500 tabular-nums shrink-0">
                    {fmtMoney(e.totals?.grand_total_rehab)}
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  v{e.version} · {e.status} · {timeAgo(e.created_at)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────── template drawer ─────────

function TemplateDrawer({ templates, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return templates;
    const t = q.trim().toLowerCase();
    return templates.filter((tp) => `${tp.category} ${tp.description}`.toLowerCase().includes(t));
  }, [templates, q]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end"
         data-testid="repair-template-drawer">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Library</div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">Template Items</div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="repair-template-drawer-close"
                  className="text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
        </div>
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" autoFocus
                   value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={`Search ${templates.length} templates…`}
                   data-testid="repair-template-search"
                   className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-[12px] text-slate-500">No templates match.</div>
          ) : filtered.map((tpl) => (
            <button key={tpl.id} type="button"
                    onClick={() => onPick(tpl)}
                    data-testid={`repair-template-opt-${tpl.id}`}
                    className="w-full text-left px-4 py-2.5 hover:bg-amber-50 transition-colors">
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest font-bold text-amber-600 shrink-0 w-16">
                  {tpl.category}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">
                    {tpl.description}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {tpl.default_qty} {tpl.default_unit.replace("_", " ")} · ${tpl.default_unit_cost}/unit
                  </div>
                </div>
                <Plus className="w-4 h-4 text-amber-600 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── small helpers ────────────

function FormGroup({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function KpiTile({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2"
         data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-slate-500">
        <span className="h-1 w-1 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </div>
      <div className="mt-0.5 text-[15px] font-bold text-slate-900 tabular-nums flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 text-slate-400" />}
        <span>{value}</span>
      </div>
    </div>
  );
}
